import type { ThunkAction, Action } from "@reduxjs/toolkit";
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";

// test changes
// RTK Query
import { rtkqApi } from "./rtkq/api/rtkqApi";

// user
import accountSettingsSlice from "./toolkit/user/accountSettingsSlice";
import socnetSlice from "./toolkit/user/socnetSlice";
import avatarSlice from "./toolkit/user/avatarSlice";
import balanceSlice from "./toolkit/user/balanceSlice";
import basketSlice from "./toolkit/user/basketSlice";
import boughtCollectionsSlice from "./toolkit/user/boughtCollectionsSlice";
import credentialsSlice from "./toolkit/user/credentialsSlice";
import librarySlice from "./toolkit/user/librarySlice";
import notificationsSlice from "./toolkit/user/notificationsSlice";
import recieptSlice from "./toolkit/pages/recieptSlice";
import abonementSlice from "./toolkit/user/abonementSlice";
import subscriptionSlice from "./toolkit/user/subscriptionSlice";
import testsSlice from "./toolkit/user/testsSlice";
import webpushesSlice from "./toolkit/user/webpushesSlice";
import authorizationSlice from "./toolkit/user/authorizationSlice";
import registrationSlice from "./toolkit/user/registrationSlice";
import userDataSlice from "./toolkit/foundationUser/foundationUserSlice";
//player
import playerSlice from "./toolkit/player/playerSlice";
// browser
import browserSlice from "./toolkit/browser/browserSlice";
// global
import authSlice from "./toolkit/global/auth/authSlice";
import headerAuthSlice from "./toolkit/global/headerAuth/headerAuthSlice";
import searchSlice from "./toolkit/global/searchSlice";
import searchPageSlice from "./toolkit/global/searchPageSlice";
import zeroSearchPageSlice from "./toolkit/global/zeroSearchPageSlice";
import interfaceSlice from "./toolkit/global/interfaceSlice";
import lastViewedBooksWidgetSlice from "./toolkit/global/lastViewedBooksWidgetSlice";
import loginSlice from "./toolkit/global/loginSlice";
import phonecodesSlice from "./toolkit/global/phonecodesSlice";
import onboardingSlice from "./toolkit/global/onboardingSlice";
import subscriptionsToItemSlice from "./toolkit/global/subscriptionsToItemSlice";
import userActionsSlice from "./toolkit/user/userActionsSlice";
import genresSlice from "./toolkit/global/genresSlice";
import bannersSlice from "./toolkit/global/bannersSlice";
import offersSlice from "./toolkit/user/offersSlice";
import historySlice from "./toolkit/pages/personalCabinet/paymentsHistorySlice";
import userLibrariesSlice from "./toolkit/pages/personalCabinet/userLibrariesSlice";
import loyaltyCardsSlice from "./toolkit/pages/personalCabinet/loyaltyCardsSlice";
import rebillsSlice from "./toolkit/user/rebillsSlice";
import artsReviewsSlice from "./toolkit/global/arts/reviews/artsReviewsSlice";
import reviewsSlice from "./toolkit/global/reviews/reviewsSlice";
import artsSlice from "./toolkit/global/arts/artsSlice";
import availableBonusesSlice from "./toolkit/global/availableBonusesSlice";
import partnersSubscriptionSlice from "./toolkit/user/partnersSubscriptionsSlice";
import bonusesSlice from "./toolkit/pages/personalCabinet/bonusesSlice";
import userSpamSlice from "./toolkit/pages/personalCabinet/userSpamSlice";
import faceSlice from "./toolkit/global/faceSlice";
import takeSlice from "./toolkit/global/take/takeSlice";
import paymentSlice from "./toolkit/payment/paymentSlice";
import audioNotesSlice from "./toolkit/player/audioNotesSlice";
import { headHunterApi } from "./rtkq/headHunter/headHunter.api";
import globalModalsSlice from "./toolkit/global/globalModals";

const appReducers = combineReducers({
  // Foundation API reducer for RTK Query
  [rtkqApi.reducerPath]: rtkqApi.reducer,
  // Headhunter API RTKQ
  [headHunterApi.reducerPath]: headHunterApi.reducer,
  // Default thunks and slices
  user: combineReducers({
    accountSettings: accountSettingsSlice,
    credentials: credentialsSlice,
    balance: balanceSlice,
    basket: basketSlice,
    library: librarySlice,
    boughtCollections: boughtCollectionsSlice,
    notifications: notificationsSlice,
    abonement: abonementSlice,
    subscription: subscriptionSlice,
    partnersSubscriptions: partnersSubscriptionSlice,
    socnet: socnetSlice,
    avatar: avatarSlice,
    tests: testsSlice,
    webpushes: webpushesSlice,
    offers: offersSlice,
    actions: userActionsSlice,
    authorization: authorizationSlice,
    registration: registrationSlice,
    rebills: rebillsSlice,
  }),
  foundationUser: userDataSlice,
  browser: browserSlice,
  player: combineReducers({
    data: playerSlice,
    notes: audioNotesSlice,
  }),
  payment: paymentSlice,
  pages: combineReducers({
    personalCabinet: combineReducers({
      paymentsHistory: historySlice,
      userLibraries: userLibrariesSlice,
      loyaltyCards: loyaltyCardsSlice,
      spam: userSpamSlice,
      bonuses: bonusesSlice,
    }),
    paymentReceipts: recieptSlice,
  }),
  global: combineReducers({
    auth: authSlice,
    header: combineReducers({
      auth: headerAuthSlice,
      banners: bannersSlice,
    }),
    globalModals: globalModalsSlice,
    availableBonuses: availableBonusesSlice,
    login: loginSlice,
    search: searchSlice,
    interface: interfaceSlice,
    lastViewedBooksWidget: lastViewedBooksWidgetSlice,
    phonecodes: phonecodesSlice,
    onboarding: onboardingSlice,
    subscriptionsToItem: subscriptionsToItemSlice,
    genres: genresSlice,
    arts: combineReducers({
      details: artsSlice,
      reviews: artsReviewsSlice,
    }),
    take: combineReducers({
      take: takeSlice,
    }),
    reviews: reviewsSlice,
    searchPage: searchPageSlice,
    zeroSearchPage: zeroSearchPageSlice,
  }),
  face: faceSlice,
});

const rootReducer: typeof appReducers = (state, action) => {
  // From:
  // https://www.appsloveworld.com/reactjs/100/9/how-to-reset-state-of-redux-store-when-using-configurestore-from-reduxjs-toolkit
  if (action.type === "RESET_APP") {
    state = undefined;
    return appReducers(state, action);
  }

  return appReducers(state, action);
};
export const makeStore = (name = "Litres Store", preloadedState = {}) =>
  configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        /* Игнорим ошибки "A non-serializable value was detected", которые возникают при сериализации заголовков Axios
         * т.к. AxiosHeaders это класс.
         * Cхожее issue https://github.com/jhipster/generator-jhipster/issues/22490
         * Документация Redux https://redux.js.org/style-guide/#do-not-put-non-serializable-values-in-state-or-actions */
        serializableCheck: {
          ignoredActionPaths: [
            "payload.meta.headers",
            // https://github.com/reduxjs/redux-toolkit/issues/1239 даже мейнтейнеры не могут решить проблему, возникла при отправке FormData, т.к. она не сериализуема
            "meta.arg.originalArgs",
            // В некоторых ситуациях нужно прокидывать callback в action. Например, если открываем компонент, вызывая action, и нам нужно узнать, подписаться на ивенты в нём.
            "payload.onHide",
            "meta.arg.onSuccess",
            "meta.arg.onError",
          ],
          ignoredPaths: [/.*error\.meta\.headers$/, /meta\.arg\.originalArgs/],
        },
      })
        .concat(rtkqApi.middleware)
        .concat(headHunterApi.middleware),
    devTools: {
      name,
    },
    preloadedState,
  });

export type TStore = ReturnType<typeof makeStore>;

export type AppDispatch = TStore["dispatch"];
export type RootState = ReturnType<TStore["getState"]>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
